/**
 * Representations generated from model data only: shell diagrams, charge balance blocks,
 * dot-and-cross / Lewis diagrams and VSEPR shape sketches. No artwork.
 */
import {
  directionBetween,
  formatCharge,
  formatShells,
  lonePairAngles,
  shapeSketch,
  type MolecularStructure,
  type ShapeName,
} from './ionsBonding';

const DEG = Math.PI / 180;

function offset(x: number, y: number, distance: number, angle: number) {
  return { x: x + distance * Math.cos(angle * DEG), y: y + distance * Math.sin(angle * DEG) };
}

/* ------------------------------------------------------------ shells */

const SHELL_SIZE = 220;
const SHELL_CENTRE = SHELL_SIZE / 2;
const NUCLEUS_RADIUS = 20;
const FIRST_SHELL_RADIUS = 38;
const SHELL_STEP = 22;
const ELECTRON_RADIUS = 5;
const BRACKET_INSET = 6;

export function ShellDiagram({
  symbol,
  shells,
  charge = 0,
  label,
}: {
  symbol: string;
  shells: number[];
  charge?: number;
  label: string;
}) {
  const outerRadius = FIRST_SHELL_RADIUS + SHELL_STEP * Math.max(shells.length - 1, 0);
  return (
    <figure className="min-w-0">
      <svg
        viewBox={`0 0 ${SHELL_SIZE} ${SHELL_SIZE}`}
        className="mx-auto w-full max-w-[13rem]"
        role="img"
        aria-label={`${label}: ${symbol}${formatCharge(charge)}, shells ${formatShells(shells)}`}
      >
        {shells.map((count, shellIndex) => {
          const radius = FIRST_SHELL_RADIUS + SHELL_STEP * shellIndex;
          const isOuter = shellIndex === shells.length - 1;
          return (
            <g key={shellIndex}>
              <circle
                cx={SHELL_CENTRE}
                cy={SHELL_CENTRE}
                r={radius}
                fill="none"
                stroke="currentColor"
                strokeDasharray={isOuter ? undefined : '3 3'}
                className="text-stone2"
              />
              {Array.from({ length: count }, (_, index) => {
                const point = offset(SHELL_CENTRE, SHELL_CENTRE, radius, (360 * index) / count - 90);
                return (
                  <circle
                    key={index}
                    cx={point.x}
                    cy={point.y}
                    r={ELECTRON_RADIUS}
                    className={isOuter ? 'fill-brand-coral' : 'fill-brand-sky'}
                  />
                );
              })}
            </g>
          );
        })}
        <circle cx={SHELL_CENTRE} cy={SHELL_CENTRE} r={NUCLEUS_RADIUS} className="fill-wash-sunshine" stroke="currentColor" />
        <text x={SHELL_CENTRE} y={SHELL_CENTRE + 5} textAnchor="middle" className="fill-ink text-[14px] font-extrabold">
          {symbol}
        </text>
        {charge !== 0 ? (
          <g className="text-ink" aria-hidden="true">
            <path
              d={`M ${SHELL_CENTRE - outerRadius - BRACKET_INSET + 8} ${SHELL_CENTRE - outerRadius - BRACKET_INSET} h -8 v ${2 * (outerRadius + BRACKET_INSET)} h 8`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path
              d={`M ${SHELL_CENTRE + outerRadius + BRACKET_INSET - 8} ${SHELL_CENTRE - outerRadius - BRACKET_INSET} h 8 v ${2 * (outerRadius + BRACKET_INSET)} h -8`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            />
            <text
              x={SHELL_CENTRE + outerRadius + BRACKET_INSET + 4}
              y={SHELL_CENTRE - outerRadius}
              className="fill-ink text-[16px] font-extrabold"
            >
              {formatCharge(charge)}
            </text>
          </g>
        ) : null}
      </svg>
      <figcaption className="mt-1 text-center text-xs font-bold text-slate2">
        {label} · {formatShells(shells)}
        <span className="sr-only"> (outer-shell electrons drawn in a second colour on a solid ring)</span>
      </figcaption>
    </figure>
  );
}

/* ---------------------------------------------------- charge balance */

export function ChargeBalance({
  positive,
  negative,
  cationLabel,
  anionLabel,
}: {
  positive: number;
  negative: number;
  cationLabel: string;
  anionLabel: string;
}) {
  const balanced = positive === negative;
  const rows = [
    { key: 'positive', label: `Positive charge from ${cationLabel}`, total: positive, sign: '+', tone: 'bg-wash-coral border-brand-coral/40' },
    { key: 'negative', label: `Negative charge from ${anionLabel}`, total: negative, sign: '−', tone: 'bg-wash-sky border-brand-sky/40' },
  ];
  return (
    <div
      className="rounded-2xl border border-hairline bg-surface p-4"
      role="img"
      aria-label={`Charge balance: +${positive} and −${negative}, ${balanced ? 'balanced' : 'not balanced'}`}
    >
      {rows.map((row) => (
        <div key={row.key} className="mt-2 first:mt-0">
          <p className="text-xs font-extrabold text-slate2">
            {row.label}: {row.sign}
            {row.total}
          </p>
          <div className="mt-1 flex flex-wrap gap-1" aria-hidden="true">
            {Array.from({ length: row.total }, (_, index) => (
              <span
                key={index}
                className={`flex h-7 w-7 items-center justify-center rounded-lg border text-sm font-extrabold text-ink ${row.tone}`}
              >
                {row.sign}
              </span>
            ))}
          </div>
        </div>
      ))}
      <p className="mt-3 text-xs font-extrabold text-ink">
        {balanced ? 'Totals match: overall charge 0' : `Overall charge ${positive - negative > 0 ? '+' : '−'}${Math.abs(positive - negative)}`}
      </p>
    </div>
  );
}

/* --------------------------------------------- dot-and-cross / Lewis */

const SCALE = 72;
const DOT_CROSS_RADIUS = 0.6 * SCALE;
const LONE_PAIR_DISTANCE_DOT_CROSS = 0.42 * SCALE;
const LONE_PAIR_DISTANCE_LEWIS = 20;
const PAIR_SPREAD = 4.5;
const PAIR_GAP = 15;
const MARKER_SIZE = 3.6;
const LEWIS_BOND_INSET = 15;
const LEWIS_LINE_GAP = 5;
const LEWIS_PADDING = 40;

function ElectronMarker({ x, y, marker, lewis }: { x: number; y: number; marker: 'dot' | 'cross'; lewis: boolean }) {
  if (lewis || marker === 'dot') {
    return <circle cx={x} cy={y} r={MARKER_SIZE} className={lewis ? 'fill-ink' : 'fill-brand-coral'} />;
  }
  return (
    <g className="text-ink" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <line x1={x - MARKER_SIZE} y1={y - MARKER_SIZE} x2={x + MARKER_SIZE} y2={y + MARKER_SIZE} />
      <line x1={x - MARKER_SIZE} y1={y + MARKER_SIZE} x2={x + MARKER_SIZE} y2={y - MARKER_SIZE} />
    </g>
  );
}

export function MoleculeDiagram({
  structure,
  variant,
  label,
  skeleton = false,
}: {
  structure: MolecularStructure;
  variant: 'dot-cross' | 'lewis';
  label: string;
  /** Connectivity only: single lines, no lone pairs or multiple bonds. */
  skeleton?: boolean;
}) {
  const lewis = variant === 'lewis';
  const padding = lewis ? LEWIS_PADDING : DOT_CROSS_RADIUS + 10;
  const xs = structure.atoms.map((item) => item.x * SCALE);
  const ys = structure.atoms.map((item) => item.y * SCALE);
  const minX = Math.min(...xs) - padding;
  const minY = Math.min(...ys) - padding;
  const width = Math.max(...xs) - Math.min(...xs) + 2 * padding;
  const height = Math.max(...ys) - Math.min(...ys) + 2 * padding;
  const bracketed = lewis && structure.charge !== 0;
  const extra = bracketed ? 24 : 0;

  return (
    <svg
      viewBox={`${minX - extra / 2} ${minY - extra / 2} ${width + extra} ${height + extra}`}
      className="mx-auto w-full max-w-[22rem]"
      role="img"
      aria-label={label}
    >
      {!lewis
        ? structure.atoms.map((item, index) => (
            <circle
              key={`shell-${index}`}
              cx={item.x * SCALE}
              cy={item.y * SCALE}
              r={DOT_CROSS_RADIUS}
              className="fill-wash-sky text-brand-sky"
              fillOpacity={0.45}
              stroke="currentColor"
            />
          ))
        : null}

      {structure.bonds.map((bond, bondIndex) => {
        const a = structure.atoms[bond.from];
        const b = structure.atoms[bond.to];
        const angle = directionBetween(structure, bond.from, bond.to);
        const perpendicular = angle + 90;
        const lines = skeleton ? 1 : bond.order;
        if (lewis) {
          const start = offset(a.x * SCALE, a.y * SCALE, LEWIS_BOND_INSET, angle);
          const end = offset(b.x * SCALE, b.y * SCALE, -LEWIS_BOND_INSET, angle);
          return (
            <g key={bondIndex} className="text-ink" stroke="currentColor" strokeWidth="2">
              {Array.from({ length: lines }, (_, line) => {
                const shift = (line - (lines - 1) / 2) * LEWIS_LINE_GAP;
                const s = offset(start.x, start.y, shift, perpendicular);
                const e = offset(end.x, end.y, shift, perpendicular);
                return <line key={line} x1={s.x} y1={s.y} x2={e.x} y2={e.y} />;
              })}
            </g>
          );
        }
        const mid = { x: ((a.x + b.x) / 2) * SCALE, y: ((a.y + b.y) / 2) * SCALE };
        return (
          <g key={bondIndex}>
            {Array.from({ length: bond.order }, (_, pair) => {
              const centre = offset(mid.x, mid.y, (pair - (bond.order - 1) / 2) * PAIR_GAP, perpendicular);
              const towardA = offset(centre.x, centre.y, -PAIR_SPREAD, angle);
              const towardB = offset(centre.x, centre.y, PAIR_SPREAD, angle);
              return (
                <g key={pair}>
                  <ElectronMarker x={towardA.x} y={towardA.y} marker={a.marker} lewis={false} />
                  <ElectronMarker x={towardB.x} y={towardB.y} marker={b.marker} lewis={false} />
                </g>
              );
            })}
          </g>
        );
      })}

      {!skeleton
        ? structure.atoms.map((item, index) =>
            lonePairAngles(structure, index).map((angle) => {
              const distance = lewis ? LONE_PAIR_DISTANCE_LEWIS : LONE_PAIR_DISTANCE_DOT_CROSS;
              const centre = offset(item.x * SCALE, item.y * SCALE, distance, angle);
              const first = offset(centre.x, centre.y, PAIR_SPREAD, angle + 90);
              const second = offset(centre.x, centre.y, -PAIR_SPREAD, angle + 90);
              return (
                <g key={`lp-${index}-${angle}`}>
                  <ElectronMarker x={first.x} y={first.y} marker={item.marker} lewis={lewis} />
                  <ElectronMarker x={second.x} y={second.y} marker={item.marker} lewis={lewis} />
                </g>
              );
            }),
          )
        : null}

      {structure.atoms.map((item, index) => (
        <g key={`atom-${index}`}>
          {lewis ? <circle cx={item.x * SCALE} cy={item.y * SCALE} r="12" className="fill-canvas-pure" /> : null}
          <text
            x={item.x * SCALE}
            y={item.y * SCALE + 5}
            textAnchor="middle"
            className="fill-ink text-[15px] font-extrabold"
          >
            {item.element}
          </text>
        </g>
      ))}

      {bracketed ? (
        <g className="text-ink" aria-hidden="true">
          <path
            d={`M ${minX + 6} ${minY} h -6 v ${height} h 6`}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          />
          <path
            d={`M ${minX + width - 6} ${minY} h 6 v ${height} h -6`}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          />
          <text x={minX + width + 3} y={minY + 6} className="fill-ink text-[16px] font-extrabold">
            {formatCharge(structure.charge)}
          </text>
        </g>
      ) : null}
    </svg>
  );
}

/* ----------------------------------------------------- shape sketch */

const SKETCH_SIZE = 200;
const SKETCH_CENTRE = SKETCH_SIZE / 2;
const SKETCH_BOND = 62;
const SKETCH_ATOM_RADIUS = 13;
const WEDGE_HALF_WIDTH = 6;
const SKETCH_LONE_PAIR_DISTANCE = 30;

export function ShapeSketchView({
  shape,
  lonePairs,
  angleDegrees,
  centralSymbol,
  terminalSymbols,
  angleLabel,
}: {
  shape: ShapeName;
  lonePairs: number;
  angleDegrees: number;
  centralSymbol: string;
  terminalSymbols: string[];
  angleLabel: string;
}) {
  const sketch = shapeSketch(shape, lonePairs, angleDegrees);
  return (
    <figure className="min-w-0">
      <svg
        viewBox={`0 0 ${SKETCH_SIZE} ${SKETCH_SIZE}`}
        className="mx-auto w-full max-w-[13rem]"
        role="img"
        aria-label={`${shape} shape sketch around ${centralSymbol}, bond angle ${angleLabel}`}
      >
        {sketch.bonds.map((bond, index) => {
          const end = offset(SKETCH_CENTRE, SKETCH_CENTRE, SKETCH_BOND, bond.angle);
          const symbol = terminalSymbols[index] ?? '';
          return (
            <g key={index}>
              {bond.style === 'wedge' ? (
                <polygon
                  points={[
                    `${SKETCH_CENTRE},${SKETCH_CENTRE}`,
                    (() => {
                      const p = offset(end.x, end.y, WEDGE_HALF_WIDTH, bond.angle + 90);
                      return `${p.x},${p.y}`;
                    })(),
                    (() => {
                      const p = offset(end.x, end.y, -WEDGE_HALF_WIDTH, bond.angle + 90);
                      return `${p.x},${p.y}`;
                    })(),
                  ].join(' ')}
                  className="fill-ink"
                />
              ) : (
                <line
                  x1={SKETCH_CENTRE}
                  y1={SKETCH_CENTRE}
                  x2={end.x}
                  y2={end.y}
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeDasharray={bond.style === 'dash' ? '4 4' : undefined}
                  className="text-ink"
                />
              )}
              <circle cx={end.x} cy={end.y} r={SKETCH_ATOM_RADIUS} className="fill-wash-sky text-brand-sky" stroke="currentColor" />
              <text x={end.x} y={end.y + 4} textAnchor="middle" className="fill-ink text-[11px] font-extrabold">
                {symbol}
              </text>
            </g>
          );
        })}
        {sketch.lonePairs.map((angle) => {
          const centre = offset(SKETCH_CENTRE, SKETCH_CENTRE, SKETCH_LONE_PAIR_DISTANCE, angle);
          const first = offset(centre.x, centre.y, PAIR_SPREAD, angle + 90);
          const second = offset(centre.x, centre.y, -PAIR_SPREAD, angle + 90);
          return (
            <g key={angle}>
              <ellipse
                cx={centre.x}
                cy={centre.y}
                rx="14"
                ry="9"
                transform={`rotate(${angle + 90} ${centre.x} ${centre.y})`}
                className="fill-wash-coral text-brand-coral"
                stroke="currentColor"
                strokeDasharray="2 2"
              />
              <circle cx={first.x} cy={first.y} r="2.6" className="fill-ink" />
              <circle cx={second.x} cy={second.y} r="2.6" className="fill-ink" />
            </g>
          );
        })}
        <circle cx={SKETCH_CENTRE} cy={SKETCH_CENTRE} r={SKETCH_ATOM_RADIUS + 2} className="fill-wash-sunshine text-ink" stroke="currentColor" />
        <text x={SKETCH_CENTRE} y={SKETCH_CENTRE + 4} textAnchor="middle" className="fill-ink text-[12px] font-extrabold">
          {centralSymbol}
        </text>
      </svg>
      <figcaption className="mt-1 text-center text-xs font-bold leading-5 text-slate2">
        3D sketch: solid wedge points towards you, dashed bond points away. Shaded lobes are lone pairs.
      </figcaption>
    </figure>
  );
}
